import { initTranslations } from '@bluebrick/saas-ui'
import { ApmVuePlugin } from '@elastic/apm-rum-vue'
import * as mdijs from '@mdi/js'
import * as Sentry from '@sentry/vue'
import i18next from 'i18next'
import mdiVue from 'mdi-vue/v3'
import { createPinia } from 'pinia'
import { createApp, type App as VueApp } from 'vue'
import '@bluebrick/saas-ui/saas-ui.css'
// Keep saas-ui import before App.vue
// eslint-disable-next-line import/order
import App from './App.vue'
import { getTelemetryConfigData, useTelemetry } from '@/features/telemetry/useTelemetry'
import { router } from '@/routes'
// The `main.scss` import must be the last import. Surely must be placed after the `'@/routes'` import.
// Otherwise pages' styles will be messed up 😱
import './assets/main.scss'

async function initI18n() {
	await i18next.init({
		lng: 'en',
		fallbackLng: 'en',
		cleanCode: true,
		defaultNS: 'base',
		resources: {},
		returnNull: false
	})

	const translationResources = import.meta.glob(['@/**/translations/*.json'], { eager: true, import: 'default' }) as Record<string, string>

	Object.keys(translationResources).forEach((file) => {
		const match = /translations\/(.*).json/gi.exec(file)

		if (!match) return

		i18next.addResourceBundle(match[1], 'base', translationResources[`${file}`], true, true)
	})

	// Init translations of the @bluebrick/saas-ui package
	initTranslations(i18next as Parameters<typeof initTranslations>[0])
}

initI18n()

const vueApp = createApp(App)

Sentry.init({
	app: vueApp,
	dsn: import.meta.env.VITE_SENTRY_DSN,
	release: `discount-advisor@${import.meta.env.VITE_APP_VERSION}`,
	environment: import.meta.env.VITE_ENVIRONMENT,
	debug: true
})

vueApp.config.throwUnhandledErrorInProduction = true
vueApp.config.errorHandler = (err, _instance, info) => {
	// Need to rethrow the error here. Otherwise it will be swallowed here and Sentry won't
	// know about it
	Sentry.captureException(err)

	const { trackException } = useTelemetry()

	trackException(err, 'Uncaught exception in vue app', {
		data: info
	})
}

vueApp
	.use(createPinia())
	.use(router)
	.use({
		install(app: VueApp) {
			app.config.globalProperties.$t = i18next.t
		}
	})
	.use(mdiVue, { icons: mdijs })

if (import.meta.env.VITE_ELASTIC_APM_URL && import.meta.env.VITE_ELASTICSEARCH_URL) {
	vueApp.use(ApmVuePlugin, {
		router,
		config: getTelemetryConfigData('ELASTIC_APM')
	})
}

vueApp.mount('#app')
